// Generated by tutorials/record.cjs from tutorials/articles/h06-ehr-held-diet-orders.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h06 --text-only` (wording) or
// `node tutorials/record.cjs h06` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'ehr-held-diet-orders',
  catalogId: 'h06',
  title: 'Held EHR diet orders',
  summary: 'Lock a resident\'s diet order, and a new diet order from your EHR is held for you to review instead of replacing it. Here is why you would, what the resident gets meanwhile, where held orders show, and how to use or keep them.',
  audience: 'Dietitians and dietary managers at facilities that use diet order locking',
  area: 'residents',
  learn: [
    'Why and when to lock a diet order',
    'What a resident gets while their diet order is locked',
    'Find held EHR diet orders on the dashboard and the resident',
    'Use the new order or keep the current one',
    'What unlocking does to the orders still held',
  ],
  keywords: [
    'ehr',
    'emr',
    'lock',
    'locked',
    'diet order lock',
    'unlock',
    'held',
    'held ehr diet changes',
    'pending diet',
    'diet fluctuations',
    'review',
    'use the new diet order',
    'keep the current diet order',
    'swallow study',
    'texture',
  ],
  steps: [
    {
      id: 'why',
      part: 'Lock a diet order',
      heading: 'Why lock a diet order',
      text: 'Dietech always uses the newest diet order, so a new order from your EHR replaces a change you made in Dietech.\n\nHere the dietitian added Low Sodium 2g for Gerald Thornton an hour ago, but his EHR order still says Renal only. Locking his order keeps the next EHR order from undoing it until someone has looked.',
      media: {
        image: {
          src: '/help/ehr-held-diet-orders/why.webp',
          width: 1280,
          height: 294,
        },
        clip: {
          src: '/help/ehr-held-diet-orders/why.mp4',
          width: 1280,
          height: 294,
          durationSec: 4,
        },
        alt: 'Diet Fluctuations for Thornton, Gerald: the newest order, Manual, Renal, Low Sodium 2g, above the EHR admission order for Renal.',
      },
    },
    {
      id: 'lock',
      part: 'Lock a diet order',
      heading: 'Select Lock',
      text: 'On Diet Fluctuations, select Lock. Lock always acts on the current diet order. The window says what locking does, and how long your EHR has to catch up: if it sends this same diet within 24 hours, the lock comes off by itself.',
      media: {
        image: {
          src: '/help/ehr-held-diet-orders/lock.webp',
          width: 942,
          height: 434,
        },
        clip: {
          src: '/help/ehr-held-diet-orders/lock.mp4',
          width: 942,
          height: 434,
          durationSec: 2,
        },
        alt: 'The Lock Diet Order window: lock the current diet order, Renal, Low Sodium 2g, Regular, Thin Liquids? EHR diet changes are held for review, and the same diet within 24 hours removes the lock.',
      },
    },
    {
      id: 'locked',
      part: 'Lock a diet order',
      heading: 'The order is locked',
      text: 'Select Lock. A lock shows on the order in the Lock status column. Point at it to see when it was locked.',
      media: {
        image: {
          src: '/help/ehr-held-diet-orders/locked.webp',
          width: 1280,
          height: 294,
        },
        clip: {
          src: '/help/ehr-held-diet-orders/locked.mp4',
          width: 1280,
          height: 294,
          durationSec: 4.3,
        },
        alt: 'Diet Fluctuations for Thornton, Gerald: the current order now has a lock in the Lock status column.',
      },
    },
    {
      id: 'held',
      part: 'While it is locked',
      heading: 'New EHR orders are held',
      text: 'While the order is locked, each new diet order from your EHR is held for review instead of replacing it. So is an order that only stops part of the diet: the locked order is never ended by your EHR.',
      textOnly: true,
    },
    {
      id: 'meanwhile',
      part: 'While it is locked',
      heading: 'The tray keeps the locked order',
      text: 'Until someone reviews a held order, nothing changes for the resident. Tray tickets, menus, Selective Touch and reports all keep using the locked diet order.',
      textOnly: true,
    },
    {
      id: 'caught-up',
      part: 'While it is locked',
      heading: 'When your EHR catches up',
      text: 'If your EHR sends the same diet as the locked order within the hours your facility set, Dietech takes the lock off and clears anything held, because your EHR has caught up. After that time, even the same diet is held for you to review.',
      textOnly: true,
    },
    {
      id: 'dashboard',
      part: 'Find held orders',
      heading: 'Held EHR diet changes on your dashboard',
      text: 'Your dashboard lists every resident with a held order under Held EHR diet changes: how many are held, the newest held diet order, and when it arrived. The card shows only at facilities that use diet order locking.',
      media: {
        image: {
          src: '/help/ehr-held-diet-orders/dashboard.webp',
          width: 1280,
          height: 160,
        },
        alt: 'The Held EHR diet changes card: Brennan, Walter with 2 held, the newest Regular, Pureed, Mildly Thick Liquids, and Achebe, Raymond with 1, each with a Review button.',
      },
    },
    {
      id: 'resident',
      part: 'Find held orders',
      heading: 'On the resident\'s Diet Fluctuations',
      text: 'On the resident, Diet Fluctuations shows the lock on the order, with an hourglass beside it when orders are held. Point at the hourglass to see how many. Held EHR changes in the toolbar says how many are waiting, and opens them.',
      media: {
        image: {
          src: '/help/ehr-held-diet-orders/resident.webp',
          width: 1280,
          height: 294,
        },
        clip: {
          src: '/help/ehr-held-diet-orders/resident.mp4',
          width: 1280,
          height: 294,
          durationSec: 4.8,
        },
        alt: 'Diet Fluctuations for Brennan, Walter: his locked order with a lock and an hourglass, and Held EHR changes (2) in the toolbar.',
      },
    },
    {
      id: 'meal-pattern',
      part: 'Find held orders',
      heading: 'And on the Meal Pattern',
      text: 'The resident\'s Meal Pattern says the same at the top, with a Review button, so whoever works on the meal pattern sees it too.',
      media: {
        image: {
          src: '/help/ehr-held-diet-orders/meal-pattern.webp',
          width: 1280,
          height: 222,
        },
        clip: {
          src: '/help/ehr-held-diet-orders/meal-pattern.mp4',
          width: 1280,
          height: 222,
          durationSec: 3.6,
        },
        alt: 'The Meal Pattern for Brennan, Walter, with the note 2 held EHR diet changes waiting for review and a Review button.',
      },
    },
    {
      id: 'open-review',
      part: 'Review a held order',
      heading: 'Open the review',
      text: 'On Diet Fluctuations, select Held EHR changes. Review on the dashboard card or the Meal Pattern opens the same window. It shows the current diet order, then each held order with when it arrived, oldest first.',
      media: {
        image: {
          src: '/help/ehr-held-diet-orders/open-review.webp',
          width: 942,
          height: 734,
        },
        clip: {
          src: '/help/ehr-held-diet-orders/open-review.mp4',
          width: 942,
          height: 734,
          durationSec: 2,
        },
        alt: 'The Held EHR diet changes window for Brennan, Walter: his current order, Regular, Pureed, Moderately Thick Liquids, and two held orders, Regular, Mechanical Soft, Thin Liquids from yesterday and Regular, Pureed, Mildly Thick Liquids from today.',
      },
    },
    {
      id: 'keep',
      part: 'Review a held order',
      heading: 'Keep the current order',
      text: 'Select a held order, then what should happen to it. This one is Walter\'s old admission order sent again, so choose Keep the current diet order and select Save.\n\nNothing changes for Walter and the lock stays. The held order is marked reviewed and drops off the list.',
      media: {
        image: {
          src: '/help/ehr-held-diet-orders/keep.webp',
          width: 942,
          height: 734,
        },
        clip: {
          src: '/help/ehr-held-diet-orders/keep.mp4',
          width: 942,
          height: 734,
          durationSec: 4.4,
        },
        alt: 'The older held order, Regular, Mechanical Soft, Thin Liquids, selected with Keep the current diet order chosen.',
      },
    },
    {
      id: 'use-new',
      part: 'Review a held order',
      heading: 'Use the new order',
      text: 'The newer order follows his latest swallow study. Select it, choose Use the new diet order (removes the lock), then select Save.\n\nIt becomes Walter\'s diet order straight away, for today\'s meals, and the lock comes off.',
      media: {
        image: {
          src: '/help/ehr-held-diet-orders/use-new.webp',
          width: 942,
          height: 666,
        },
        clip: {
          src: '/help/ehr-held-diet-orders/use-new.mp4',
          width: 942,
          height: 666,
          durationSec: 4.9,
        },
        alt: 'The held order Regular, Pureed, Mildly Thick Liquids selected, with Use the new diet order (removes the lock) chosen.',
      },
    },
    {
      id: 'done',
      part: 'Review a held order',
      heading: 'The new order is current',
      text: 'Diet Fluctuations now shows the order from your EHR at the top, with no lock. Both choices are logged with your name and the time.',
      media: {
        image: {
          src: '/help/ehr-held-diet-orders/done.webp',
          width: 1280,
          height: 294,
        },
        clip: {
          src: '/help/ehr-held-diet-orders/done.mp4',
          width: 1280,
          height: 294,
          durationSec: 4.6,
        },
        alt: 'Diet Fluctuations for Brennan, Walter: the new EHR order, Regular, Pureed, Mildly Thick Liquids, at the top, and his earlier order no longer locked.',
      },
    },
  ],
  tips: [
    'Lock a diet order when you have changed it on purpose and your EHR has not caught up yet, for example after a swallow study. Then ask whoever writes orders in your EHR to change it there too.',
    'Check the Held EHR diet changes card on your dashboard every day. A held order changes nothing until someone reviews it.',
    'When a resident has several held orders, review them oldest first. The window lists them in the order they arrived.',
  ],
  notes: [
    'Diet order locking is a setting for the whole facility. Dietech support turns it on, along with how many hours your EHR has to send the same diet before the lock comes off by itself (24 in these pictures).',
    'Unlocking a diet order that has held orders applies them all, oldest first, and the newest becomes the current diet order. Deleting the locked entry does the same.',
    'After you use a new diet order, Dietech runs the same checks as for any diet change, so it may offer to update the resident\'s meal pattern or Selective Menu items.',
    'Locking, unlocking and reviewing need the Diet Fluctuations level that lets you change diet orders. With view only, you can see held orders but not save a choice. Ask your administrator.',
  ],
  related: ['ehr-connection', 'change-diet-order', 'your-dashboard', 'auto-item-mods', 'ehr-page'],
  recorded: '2026-11-03',
};

export default article;
